import React, {useRef, useState, useCallback} from 'react';
import styled from 'styled-components';
import {Button, Detail, EMPHASIS, SegmentedControl} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import {ContentState, EditorState} from 'draft-js';
import {RichTextEditor} from './RichTextEditor';
import {extractMentionIds} from '../utils/insertMention';
import {mentionDecorator} from '../utils/mentionDecorator';
import {RecipientsBar} from './RecipientsBar';
import {AttachmentList} from './AttachmentList';
import {ConfirmModeSwitch} from './ConfirmModeSwitch';
import {ConfirmReplaceWithDraft} from './ConfirmReplaceWithDraft';
import {useReplySubmit} from '../hooks/useReplySubmit';
import {useCreateComment} from '../hooks/useCreateComment';
import {useAttachmentUpload} from '../hooks/useAttachmentUpload';
import {useGenerateReplyDraft} from '../hooks/useGenerateReplyDraft';

type ComposerMode = 'reply' | 'note';

const Shell = styled.div`
	margin-top: 24px;
	overflow: hidden;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`;

const ModeRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[200]};
	padding: ${p => p.theme.sys.spacing[300]} ${p => p.theme.sys.spacing[400]} 0;
`;

const RecipientsWrap = styled.div`
	padding: 12px 16px 0;
`;

const Body = styled.div<{$note: boolean}>`
	padding: 16px;
	background: ${p => (p.$note ? '#eeedff' : 'transparent')};
`;

const HiddenInput = styled.input`
	display: none;
`;

const ErrorText = styled.p`
	margin-top: 8px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #dc2626;
`;

const Footer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-top: 1px solid #e2e8f0;
	padding: 12px 16px;
	background: #f8fafc;
`;

const FooterLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const FooterRight = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const DraftedRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 8px;
	border: 1px solid #e9d5ff;
	border-radius: 6px;
	background: #f3e8ff;
	padding: 6px 10px;
`;

const DraftedLabel = styled.span`
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	color: ${p => p.theme.sys.color.brand.primary['1']};
`;

const DraftedActions = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	margin-left: auto;
`;

const LinkButton = styled.button`
	border: 0;
	border-radius: 4px;
	background: transparent;
	padding: 2px 6px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	color: ${p => p.theme.sys.color.brand.primary['1']};
	cursor: pointer;
	&:hover {
		text-decoration: underline;
	}
	&:disabled {
		cursor: not-allowed;
		opacity: 0.4;
		text-decoration: none;
	}
`;

interface Props {
	ticketId: string;
	requesterEmail: string;
	/** Named in the reply hint, so the writer knows who is about to receive this. */
	requesterName?: string;
	onSent: () => void;
	/** Used in tests to inject a pre-populated EditorState without fighting Draft.js + jsdom */
	initialEditorState?: EditorState;
	/** Used in tests to start in a specific mode */
	initialMode?: ComposerMode;
}

export function ReplyComposer({ticketId, requesterEmail, requesterName, onSent, initialEditorState, initialMode}: Props) {
	const [mode, setMode] = useState<ComposerMode>(initialMode ?? 'reply');
	const [editorState, setEditorState] = useState(() => initialEditorState ?? EditorState.createEmpty(mentionDecorator));
	const [drafts, setDrafts] = useState<Record<ComposerMode, EditorState | null>>({
		reply: null,
		note: null,
	});
	const [to, setTo] = useState<string[]>([requesterEmail]);
	const [cc, setCc] = useState<string[]>([]);
	const [bcc, setBcc] = useState<string[]>([]);
	const [pendingMode, setPendingMode] = useState<ComposerMode | null>(null);
	const [confirmReplaceWithDraft, setConfirmReplaceWithDraft] = useState(false);
	const [hasDraft, setHasDraft] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	// The editor state from before the FIRST draft landed, so Revert still returns
	// the agent's own text after any number of "Try again" rounds.
	const preDraftEditorState = useRef<EditorState | null>(null);
	const replySubmit = useReplySubmit();
	const commentSubmit = useCreateComment();
	const attachUpload = useAttachmentUpload();
	const replyDraft = useGenerateReplyDraft();

	const loading = mode === 'reply' ? replySubmit.loading : commentSubmit.loading;
	const replyError = replySubmit.error;
	const noteError = commentSubmit.error;
	const submitError = mode === 'reply' ? replyError : noteError;
	const error = submitError ?? (mode === 'reply' ? attachUpload.error ?? replyDraft.error : null);

	const isEmpty = !editorState.getCurrentContent().hasText();

	// Plain text on purpose: the model returns paragraphs separated by blank lines,
	// so building content this way keeps model output off the HTML sanitizer path.
	// The composer's existing editorToHtml still handles HTML when sending.
	const runDraft = useCallback(async () => {
		if (preDraftEditorState.current === null) {
			preDraftEditorState.current = editorState;
		}
		try {
			const draftText = await replyDraft.generate(ticketId);
			setEditorState(EditorState.createWithContent(ContentState.createFromText(draftText), mentionDecorator));
			setHasDraft(true);
		} catch {
			// The hook surfaces the reason through `error`; leave the editor untouched.
			// Drop the snapshot if nothing was ever replaced, so a later successful
			// draft snapshots the agent's current text rather than a stale copy.
			if (!hasDraft) preDraftEditorState.current = null;
		}
	}, [editorState, hasDraft, replyDraft, ticketId]);

	const handleDraftWithAi = useCallback(() => {
		if (replyDraft.inFlight) return;
		// Only text the agent might lose warrants a prompt; an AI draft can be
		// re-rolled freely because Revert still holds the pre-draft state.
		if (!isEmpty && !hasDraft) {
			setConfirmReplaceWithDraft(true);
			return;
		}
		void runDraft();
	}, [replyDraft.inFlight, isEmpty, hasDraft, runDraft]);

	const confirmDraftReplace = useCallback(() => {
		setConfirmReplaceWithDraft(false);
		void runDraft();
	}, [runDraft]);

	const cancelDraftReplace = useCallback(() => setConfirmReplaceWithDraft(false), []);

	const handleRevertDraft = useCallback(() => {
		setEditorState(preDraftEditorState.current ?? EditorState.createEmpty(mentionDecorator));
		preDraftEditorState.current = null;
		setHasDraft(false);
		replyDraft.reset();
	}, [replyDraft]);

	const requestModeSwitch = useCallback(
		(target: ComposerMode) => {
			if (target === mode) return;
			if (isEmpty) {
				const restoredDraft = drafts[target];
				if (restoredDraft) {
					setEditorState(restoredDraft);
					setDrafts(prev => ({...prev, [target]: null}));
				}
				setMode(target);
			} else {
				setPendingMode(target);
			}
		},
		[mode, isEmpty, drafts]
	);

	const handleKeepDraft = useCallback(() => {
		if (!pendingMode) return;
		// Stash current editor under outgoing mode, then switch and restore any stashed draft
		setDrafts(prev => {
			const restoredDraft = prev[pendingMode];
			setEditorState(restoredDraft ?? EditorState.createEmpty(mentionDecorator));
			return {...prev, [mode]: editorState, [pendingMode]: null};
		});
		setMode(pendingMode);
		setPendingMode(null);
	}, [pendingMode, mode, editorState]);

	const handleDiscard = useCallback(() => {
		if (!pendingMode) return;
		const restoredDraft = drafts[pendingMode];
		if (restoredDraft) {
			setEditorState(restoredDraft);
			setDrafts(prev => ({...prev, [pendingMode]: null}));
		} else {
			setEditorState(EditorState.createEmpty(mentionDecorator));
		}
		// The reply content is being thrown away, so there is no longer anything for
		// Revert to restore. (Keep draft stashes it instead, so it stays revertable.)
		if (mode === 'reply') {
			preDraftEditorState.current = null;
			setHasDraft(false);
		}
		setMode(pendingMode);
		setPendingMode(null);
	}, [pendingMode, drafts, mode]);

	const cancelModeSwitch = useCallback(() => {
		setPendingMode(null);
	}, []);

	const handleFileChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files ?? []);
			files.forEach(file => void attachUpload.upload(ticketId, file));
			if (fileInputRef.current) fileInputRef.current.value = '';
		},
		[attachUpload, ticketId]
	);

	const handleSend = useCallback(async () => {
		if (mode === 'reply' && isEmpty) return;
		if (mode === 'note' && isEmpty) return;
		if (mode === 'reply' && to.length === 0) return;

		if (mode === 'reply') {
			const completedAttachments = attachUpload.attachments
				.filter(a => a.progress === 100)
				.map(({storageKey, filename, contentType, sizeBytes}) => ({
					storageKey,
					filename,
					contentType,
					sizeBytes,
				}));
			await replySubmit.submit({
				ticketId,
				editorState,
				to,
				cc: cc.length > 0 ? cc : undefined,
				bcc: bcc.length > 0 ? bcc : undefined,
				attachments: completedAttachments.length > 0 ? completedAttachments : undefined,
			});
		} else if (mode === 'note') {
			const content = editorState.getCurrentContent();
			const mentionIds = extractMentionIds(content);
			let bodyMd = content.getPlainText('\n');
			content.getBlockMap().forEach(block => {
				if (!block) return;
				block.findEntityRanges(
					char => {
						const entityKey = char.getEntity();
						if (!entityKey) return false;
						return content.getEntity(entityKey).getType() === 'MENTION';
					},
					(start, end) => {
						const entityKey = block.getEntityAt(start);
						if (!entityKey) return;
						const entity = content.getEntity(entityKey);
						const data = entity.getData() as {userId: string; name: string};
						const displayText = block.getText().slice(start, end);
						bodyMd = bodyMd.replace(displayText, `@[user:${data.userId}]`);
					}
				);
			});
			await commentSubmit.submit(ticketId, {bodyMd, mentions: mentionIds});
		}

		setEditorState(EditorState.createEmpty(mentionDecorator));
		setDrafts({reply: null, note: null});
		setTo([requesterEmail]);
		setCc([]);
		setBcc([]);
		attachUpload.clear();
		preDraftEditorState.current = null;
		setHasDraft(false);
		onSent();
	}, [mode, editorState, ticketId, requesterEmail, to, cc, bcc, replySubmit, commentSubmit, attachUpload, isEmpty, onSent]);

	return (
		<Shell>
			<ModeRow>
				<SegmentedControl
					value={mode}
					onValueChange={value => requestModeSwitch(value as ComposerMode)}
					label="Compose mode"
				>
					<SegmentedControl.Item value="reply" label="Reply">
						Reply
					</SegmentedControl.Item>
					<SegmentedControl.Item value="note" label="Internal note">
						Internal note
					</SegmentedControl.Item>
				</SegmentedControl>
				{/* Who is about to read this, spelled out. The consequence of the mode is
				    the one thing that must not be inferred from a highlighted segment. */}
				<Detail color="medium" data-testid="composer-audience">
					{mode === 'reply'
						? `Replying to ${requesterName || 'the requester'} — they'll receive this by email`
						: 'Internal note — visible to your team only, not the customer'}
				</Detail>
			</ModeRow>

			{mode === 'reply' && (
				<RecipientsWrap>
					<RecipientsBar to={to} cc={cc} bcc={bcc} onToChange={setTo} onCcChange={setCc} onBccChange={setBcc} />
				</RecipientsWrap>
			)}

			{/* `data-mode` is the assertable handle on which way the composer is tinted.
			    Reading the background off the stylesheet instead means parsing every
			    styled-components rule on the page, including the design system's own —
			    which the CSS parser behind `toHaveStyleRule` cannot always handle. */}
			<Body data-testid="composer-body" data-mode={mode} $note={mode === 'note'}>
				<RichTextEditor
					editorState={editorState}
					onChange={setEditorState}
					placeholder={mode === 'reply' ? 'Write a reply to the customer..' : 'Write an internal note...'}
					mentionEnabled={mode === 'note'}
				/>

				{mode === 'reply' && (
					<>
						<AttachmentList attachments={attachUpload.attachments} onRemove={attachUpload.remove} />
						<HiddenInput
							ref={fileInputRef}
							type="file"
							multiple
							onChange={handleFileChange}
							aria-label="Attach files"
						/>
					</>
				)}

				{mode === 'reply' && hasDraft && (
					<DraftedRow data-testid="drafted-with-ai">
						<DraftedLabel>Drafted with AI</DraftedLabel>
						<DraftedActions>
							<LinkButton type="button" onClick={handleRevertDraft} disabled={replyDraft.inFlight}>
								Revert
							</LinkButton>
							<LinkButton type="button" onClick={handleDraftWithAi} disabled={replyDraft.inFlight}>
								Try again
							</LinkButton>
						</DraftedActions>
					</DraftedRow>
				)}

				{error && <ErrorText role="alert">{error}</ErrorText>}
			</Body>

			<Footer>
				<FooterLeft>
					{mode === 'reply' && (
						<LabelledButton
							emphasis={EMPHASIS.LOW}
							icon="paperclip"
							label="Attach files"
							onPress={() => fileInputRef.current?.click()}
						/>
					)}
					{mode === 'reply' && (
						<Button
							emphasis={EMPHASIS.MONO}
							icon="sparkle"
							disabled={replyDraft.inFlight}
							aria-busy={replyDraft.inFlight}
							onPress={handleDraftWithAi}
						>
							{replyDraft.inFlight ? 'Drafting…' : 'Draft with AI'}
						</Button>
					)}
				</FooterLeft>
				<FooterRight>
					<Button
						emphasis={EMPHASIS.HIGH}
						disabled={isEmpty || loading || replyDraft.inFlight || (mode === 'reply' && to.length === 0)}
						onPress={handleSend}
					>
						{loading ? 'Sending…' : mode === 'reply' ? 'Send reply' : 'Add note'}
					</Button>
				</FooterRight>
			</Footer>

			{confirmReplaceWithDraft && (
				<ConfirmReplaceWithDraft onConfirm={confirmDraftReplace} onCancel={cancelDraftReplace} />
			)}

			{pendingMode && (
				<ConfirmModeSwitch
					fromMode={mode}
					toMode={pendingMode}
					onKeepDraft={handleKeepDraft}
					onDiscard={handleDiscard}
					onCancel={cancelModeSwitch}
				/>
			)}
		</Shell>
	);
}
